// ---------------------------------------------------------------------------
// YouAreYou (uru) — Notification Provider
//
// Manages the notification inbox (REST) and realtime WS event handlers.
//
// On mount:
//   - Fetches unread notifications via GET /api/account/notification
//   - Subscribes to realtime WS events:
//     • "notification" (reserved type) → prepend to inbox, show toast
//     • "connection_accepted"          → show success toast, refresh inbox
//     • "connection_started"           → show info toast
//     • "connection_denied"            → show info toast
//     • "kyc_completed"                → show success toast
//
// Toasts auto-dismiss after 4 seconds.
// ---------------------------------------------------------------------------

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { listNotifications, markNotificationRead } from "@/api/endpoints";
import { realtime } from "@/services/realtime";
import { apiClient } from "@/api/client";
import { NotificationContext } from "@/context/notificationTypes";
import type { ToastItem } from "@/context/notificationTypes";
import type { AppNotification } from "@/types/api";

// ---------------------------------------------------------------------------
// Helper
// ---------------------------------------------------------------------------

let toastCounter = 0;

function makeToastId(): string {
  return `toast-${++toastCounter}`;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  // Used to cancel auto-dismiss timers on unmount
  const dismissTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  );

  // ---------------------------------------------------------------------------
  // Toast management
  // ---------------------------------------------------------------------------

  const addToast = useCallback((toast: Omit<ToastItem, "id">) => {
    const id = makeToastId();
    setToasts((prev) => [...prev, { ...toast, id }]);

    // Auto-dismiss after 4 seconds
    const timer = setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
      dismissTimers.current.delete(id);
    }, 4000);

    dismissTimers.current.set(id, timer);
  }, []);

  const dismissToast = useCallback((id: string) => {
    const timer = dismissTimers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      dismissTimers.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Clear all timers on unmount
  useEffect(() => {
    const timers = dismissTimers.current;
    return () => {
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    };
  }, []);

  // ---------------------------------------------------------------------------
  // REST inbox
  // ---------------------------------------------------------------------------

  const refresh = useCallback(async () => {
    try {
      const data = await listNotifications({ is_unread: true, limit: 50 });
      setNotifications(data);
    } catch {
      // Non-critical — keep existing notifications
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const markRead = useCallback(
    async (id: number) => {
      try {
        await markNotificationRead(id);
        setNotifications((prev) => prev.filter((n) => n.id !== id));
      } catch {
        // Non-critical
      }
    },
    [],
  );

  // ---------------------------------------------------------------------------
  // Realtime WS connection + event handlers
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const token = apiClient.getAccessToken();
    if (token) {
      realtime.connect(token);
    }

    // Disconnect on unmount (layout unmounts on logout)
    return () => {
      realtime.disconnect();
    };
  }, []);

  useEffect(() => {
    // "notification" — reserved WS type sent by user.notify().
    // Handles: connection_accepted, connection_denied, kyc_completed,
    // and any other server-created persistent notification.
    // kind determines whether to show a toast or update bell silently.
    const offNotif = realtime.on("notification", (data) => {
      const kind = (data.kind as string) ?? "general";
      const notif: AppNotification = {
        id: (data.id as number) ?? Date.now(),
        created: (data.created as string) ?? new Date().toISOString(),
        title: (data.title as string) ?? "",
        body: (data.body as string) ?? "",
        kind,
        data: (data.data as Record<string, unknown>) ?? {},
        action_url: (data.action_url as string | null) ?? null,
        is_unread: true,
        expires_at: (data.expires_at as string | null) ?? null,
      };
      setNotifications((prev) => [notif, ...prev]);

      // Toast for high-signal events; everything else updates bell silently.
      const shouldToast =
        kind === "connection_accepted" ||
        kind === "kyc_completed" ||
        kind === "data_sharing" ||
        kind === "alert";

      if (shouldToast && (notif.body || notif.title)) {
        // For data_sharing notifications, navigate directly to the ContactDetailModal
        // via /connections/:id rather than the generic action_url.
        let actionUrl = notif.action_url ?? undefined;
        if (kind === "data_sharing") {
          const connId = notif.data.connection_id;
          if (typeof connId === "number" || typeof connId === "string") {
            actionUrl = `/connections/${connId}`;
          }
        }
        addToast({
          message: notif.body || notif.title,
          kind:
            kind === "connection_accepted" ||
            kind === "kyc_completed" ||
            kind === "data_sharing"
              ? "success"
              : "info",
          actionUrl,
        });
      }
    });

    // "connection_started" — someone began KYC via your link.
    // Still a raw _publish() event (no inbox persistence needed).
    const offStarted = realtime.on("connection_started", (data) => {
      const name = (data.person_name as string) || "Someone";
      addToast({
        message: `${name} is verifying their identity…`,
        kind: "info",
      });
    });

    return () => {
      offNotif();
      offStarted();
    };
  }, [addToast]);

  // ---------------------------------------------------------------------------
  // Context value
  // ---------------------------------------------------------------------------

  const unreadCount = useMemo(
    () => notifications.filter((n) => n.is_unread).length,
    [notifications],
  );

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      isLoading,
      toasts,
      markRead,
      addToast,
      dismissToast,
      refresh,
    }),
    [notifications, unreadCount, isLoading, toasts, markRead, addToast, dismissToast, refresh],
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}
